import React, { useMemo } from "react";

import { capitalize, groupBy } from "lodash";
import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import Badge from "components/Badge";

import {
  ClearingSaleLotStatus,
  BuyerStatus,
} from "constants/clearingSaleAttributes";

import { EMPTY_ARRAY, formatDecimal } from "lib";

import {
  getSaleLotIdsByConsignmentId,
  selectConsignmentIdsSaleLotIdsByBuyerIdLookup,
  selectBuyerInvoiceTotalCentsBySaleLotIdLookup,
  getSaleLotById,
  getPayments,
  getSaleLots,
} from "selectors";

const StatusCountsWrapper = styled.div`
  ${props => props.hasContentAbove && "margin-top: 4px;"}
`;

const formatStatusCount = (status, count, amountCents = null) => {
  const capitalizedStatus = capitalize(status);
  const amountDisplay =
    amountCents !== null ? ` ($${formatDecimal(amountCents / 100)})` : "";
  return `${count} ${capitalizedStatus}${amountDisplay}`;
};

export const ConsignmentSaleLotSummary = React.memo(
  ({ consignmentId, buyerId, expanded = false }) => {
    const saleLotIds =
      useSelector(state =>
        buyerId
          ? selectConsignmentIdsSaleLotIdsByBuyerIdLookup(state)[buyerId][
              consignmentId
            ]
          : getSaleLotIdsByConsignmentId(consignmentId)(state),
      ) || EMPTY_ARRAY;

    return <SaleLotSummary saleLotIds={saleLotIds} expanded={expanded} />;
  },
);

export const SaleLotSummary = React.memo(
  ({ saleLotIds, expanded = false, showPayments = false }) => {
    const saleLotByIdLookup = useSelector(getSaleLots);
    const saleLots = useMemo(
      () => saleLotIds.map(saleLotId => saleLotByIdLookup[saleLotId]),
      [saleLotIds, saleLotByIdLookup],
    );
    const payments = useSelector(getPayments);
    const invoiceTotalsBySaleLotId = useSelector(
      selectBuyerInvoiceTotalCentsBySaleLotIdLookup,
    );

    // Show condensed view (just count) for vendor header (not expanded, not showing payments)
    if (!expanded && !showPayments) {
      return `${saleLotIds.length} Lots`;
    }

    const saleLotsGroupedByStatus = groupBy(
      saleLots,
      "clearingSaleAttributes.status",
    );

    const totalInvoiceCents = showPayments
      ? saleLots.reduce(
          (sum, lot) => sum + (invoiceTotalsBySaleLotId[lot.id] || 0),
          0,
        )
      : 0;

    const buyerId = showPayments
      ? saleLots.find(lot => lot?.buyer_id)?.buyer_id
      : null;

    const totalPaymentsCents =
      showPayments && buyerId
        ? Object.values(payments)
            .filter(payment => payment.fromBusinessId === buyerId)
            .reduce((sum, payment) => sum + (payment.totalAmountCents || 0), 0)
        : 0;

    const totalOutstandingCents = totalInvoiceCents - totalPaymentsCents;

    const statusCounts = Object.entries(saleLotsGroupedByStatus).map(
      ([status, statusLots]) => {
        const count = statusLots.length;
        // For vendors (when not showPayments), show total_price_cents per status
        // For buyers (when showPayments), don't show amounts in status counts
        const amountCents = !showPayments
          ? statusLots.reduce(
              (sum, lot) => sum + (lot.total_price_cents || 0),
              0,
            )
          : null;
        return { status, count, amountCents };
      },
    );

    return (
      <div>
        {showPayments && (
          <div>
            <strong>${formatDecimal(totalOutstandingCents / 100)} Due</strong>
          </div>
        )}
        {expanded && showPayments && (
          <>
            <div>${formatDecimal(totalInvoiceCents / 100)} Total</div>
            <div>${formatDecimal(totalPaymentsCents / 100)} Paid</div>
          </>
        )}
        {statusCounts.length > 0 && (
          <StatusCountsWrapper hasContentAbove={showPayments}>
            {showPayments
              ? // Buyers: show on one line
                statusCounts
                  .map(({ status, count, amountCents }) =>
                    formatStatusCount(status, count, amountCents),
                  )
                  .join(", ")
              : // Vendors: show each on separate lines
                statusCounts.map(({ status, count, amountCents }) => (
                  <div key={status}>
                    {formatStatusCount(status, count, amountCents)}
                  </div>
                ))}
          </StatusCountsWrapper>
        )}
      </div>
    );
  },
);

const BuyerStatusColorMap = {
  [BuyerStatus.PAID]: "paid",
  [BuyerStatus.PARTIALLY_PAID]: "warning",
  [BuyerStatus.SOLD]: "sold",
};

export const BuyerLotStatus = ({ saleLotIds }) => {
  const status = useSelector(state => {
    const saleLotsStatuses = saleLotIds.map(
      id => getSaleLotById(id)(state).clearingSaleAttributes?.status,
    );
    if (saleLotsStatuses.every(s => s === ClearingSaleLotStatus.PAID)) {
      return BuyerStatus.PAID;
    } else if (saleLotsStatuses.includes(ClearingSaleLotStatus.PAID)) {
      return BuyerStatus.PARTIALLY_PAID;
    } else {
      return BuyerStatus.SOLD;
    }
  });

  return <Badge color={BuyerStatusColorMap[status]}>{status}</Badge>;
};
